import { alpha, createTheme, type Theme } from '@mui/material/styles'
import { smartAdminAccentTokens, type CodesignThemeName } from './presets'

const baseBodyColor = '#212529'
const baseBodyBackground = '#ffffff'
const baseBorderColor = '#dee2e6'
const baseSecondaryBackground = '#e9ecef'

function getButtonToken(color: string) {
  if (color in smartAdminAccentTokens) {
    return smartAdminAccentTokens[color as CodesignThemeName]
  }

  return smartAdminAccentTokens.primary
}

function createComponents(activeThemeName: CodesignThemeName): Theme['components'] {
  const activeTheme = smartAdminAccentTokens[activeThemeName]

  return {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: baseBodyBackground,
          color: baseBodyColor,
        },
        '*, *::before, *::after': {
          boxSizing: 'border-box',
        },
        '::selection': {
          backgroundColor: alpha(activeTheme.main, 0.18),
        },
      },
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true,
        variant: 'contained',
      },
      styleOverrides: {
        root: ({ ownerState, theme }) => {
          const buttonToken = getButtonToken(ownerState.color ?? 'primary')

          return {
            borderRadius: 6,
            fontSize: '0.875rem',
            fontWeight: 500,
            lineHeight: 1.6,
            minHeight: 42,
            padding: '8px 18px',
            textTransform: 'none',
            transition: theme.transitions.create(['background-color', 'border-color', 'box-shadow', 'transform'], {
              duration: theme.transitions.duration.shorter,
            }),
            '&:focus-visible': {
              boxShadow: `0 0 0 4px ${alpha(buttonToken.main, 0.25)}`,
            },
            ...(ownerState.size === 'small' && {
              fontSize: '0.825rem',
              minHeight: 36,
              padding: '6px 14px',
            }),
            ...(ownerState.size === 'large' && {
              fontSize: '1rem',
              minHeight: 50,
              padding: '12px 24px',
            }),
            ...(ownerState.variant === 'contained' && {
              boxShadow: `0 2px 6px 0 ${buttonToken.shadow}`,
              '&:hover': {
                boxShadow: `0 2px 6px 0 ${buttonToken.shadow}`,
              },
              '&:active': {
                boxShadow: 'inset 0 2px 5px rgba(0, 0, 0, 0.15)',
                transform: 'translateY(1px)',
              },
            }),
            ...(ownerState.variant === 'outlined' && {
              borderWidth: 1,
              '&:hover': {
                borderWidth: 1,
              },
              '&:active': {
                boxShadow: 'inset 0 2px 5px rgba(0, 0, 0, 0.12)',
              },
            }),
            ...(ownerState.variant === 'text' && {
              '&:hover': {
                backgroundColor: alpha(theme.palette.text.primary, 0.05),
              },
            }),
          }
        },
      },
    },
    MuiFormControlLabel: {
      styleOverrides: {
        root: {
          columnGap: '0.625rem',
          marginLeft: 0,
          marginRight: 0,
        },
        label: {
          color: baseBodyColor,
          fontSize: '0.875rem',
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          marginLeft: 0,
          marginRight: 0,
          marginTop: 6,
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: 'rgba(33, 37, 41, 0.75)',
          fontSize: '0.875rem',
          '&.Mui-focused': {
            color: activeTheme.main,
          },
        },
        outlined: {
          transform: 'translate(14px, 16px) scale(1)',
          '&.MuiInputLabel-shrink': {
            transform: 'translate(14px, -9px) scale(0.75)',
          },
        },
        sizeSmall: {
          transform: 'translate(14px, 10px) scale(1)',
          '&.MuiInputLabel-shrink': {
            transform: 'translate(14px, -9px) scale(0.75)',
          },
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          border: `1px solid ${baseBorderColor}`,
          borderRadius: 6,
          boxShadow: `0 0 13px 0 ${alpha(activeTheme.main, 0.08)}`,
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          fontSize: '0.875rem',
        },
        standard: {
          backgroundColor: alpha(activeTheme.main, 0.1),
          color: baseBodyColor,
        },
      },
    },
    MuiBadge: {
      styleOverrides: {
        badge: {
          fontSize: '0.75rem',
          fontWeight: 600,
          minWidth: 20,
          padding: '0 6px',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          border: `1px solid ${baseBorderColor}`,
          borderRadius: 6,
          boxShadow: `0 0.125rem 0.25rem ${alpha('#000000', 0.075)}`,
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: 20,
          '&:last-child': {
            paddingBottom: 20,
          },
        },
      },
    },
    MuiCheckbox: {
      defaultProps: {
        disableRipple: true,
      },
      styleOverrides: {
        root: ({ ownerState, theme }) => {
          const controlToken = getButtonToken(ownerState.color ?? 'primary')
          const indicatorSize = ownerState.size === 'small' ? 14 : 16

          return {
            borderRadius: 0,
            color: 'transparent',
            padding: 2,
            '&:hover': {
              backgroundColor: 'transparent',
            },
            '&:active .CodesignCheckboxIndicator': {
              filter: 'brightness(90%)',
            },
            '&.Mui-focusVisible .CodesignCheckboxIndicator': {
              borderColor: controlToken.main,
              boxShadow: `0 0 0 0.25rem ${alpha(controlToken.main, 0.25)}`,
            },
            '&.Mui-disabled .CodesignCheckboxIndicator': {
              filter: 'none',
              opacity: 0.5,
            },
            '& .CodesignCheckboxIndicator': {
              alignItems: 'center',
              backgroundColor: baseBodyBackground,
              border: `1px solid ${baseBorderColor}`,
              boxSizing: 'border-box',
              color: 'transparent',
              display: 'inline-flex',
              flexShrink: 0,
              height: indicatorSize,
              justifyContent: 'center',
              transition: theme.transitions.create(['background-color', 'border-color', 'box-shadow', 'filter'], {
                duration: theme.transitions.duration.shortest,
              }),
              width: indicatorSize,
            },
            '& .CodesignCheckboxIndicator svg': {
              display: 'block',
              height: indicatorSize - 4,
              width: indicatorSize - 4,
            },
            '& .CodesignCheckboxIndicator[data-shape="default"]': {
              borderRadius: 4,
            },
            '& .CodesignCheckboxIndicator[data-shape="rounded"]': {
              borderRadius: '50%',
            },
            '& .CodesignCheckboxIndicator[data-state="checked"]': {
              backgroundColor: controlToken.main,
              borderColor: controlToken.main,
              color: '#ffffff',
            },
            '& .CodesignCheckboxIndicator[data-state="indeterminate"]': {
              backgroundColor: controlToken.main,
              borderColor: controlToken.main,
              color: '#ffffff',
            },
          }
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 8,
          boxShadow: `0 0.5rem 1rem ${alpha('#000000', 0.15)}`,
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          borderBottom: `1px solid ${baseBorderColor}`,
          fontSize: '1.125rem',
          fontWeight: 600,
          padding: '16px 20px',
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          padding: '20px !important',
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          borderTop: `1px solid ${baseBorderColor}`,
          padding: '12px 20px',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: baseBodyBackground,
          borderColor: baseBorderColor,
          boxShadow: `0 0.5rem 1rem ${alpha('#000000', 0.12)}`,
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontSize: '0.875rem',
          minHeight: 42,
          padding: '10px 18px',
          '&:hover': {
            backgroundColor: alpha(activeTheme.main, 0.06),
          },
          '&.Mui-selected': {
            backgroundColor: alpha(activeTheme.main, 0.12),
            fontWeight: 500,
            '&:hover': {
              backgroundColor: alpha(activeTheme.main, 0.18),
            },
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor: baseBodyBackground,
          borderRadius: 6,
          minHeight: 56,
          transition: theme.transitions.create(['border-color', 'box-shadow'], {
            duration: theme.transitions.duration.shorter,
          }),
          '&.MuiInputBase-sizeSmall': {
            minHeight: 40,
          },
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: baseBorderColor,
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: alpha(theme.palette.text.primary, 0.3),
          },
          '&.Mui-focused': {
            boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.14)}`,
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.primary.main,
          },
          '&.Mui-disabled': {
            backgroundColor: baseSecondaryBackground,
          },
        }),
        input: ({ ownerState }) => ({
          fontSize: '0.875rem',
          lineHeight: 1.5,
          padding: ownerState.size === 'small' ? '9.5px 14px' : '16.5px 14px',
        }),
      },
    },
    MuiRadio: {
      defaultProps: {
        disableRipple: true,
      },
      styleOverrides: {
        root: ({ ownerState, theme }) => {
          const controlToken = getButtonToken(ownerState.color ?? 'primary')
          const indicatorSize = ownerState.size === 'small' ? 14 : 16

          return {
            borderRadius: 0,
            color: 'transparent',
            padding: 2,
            '&:hover': {
              backgroundColor: 'transparent',
            },
            '&:active .CodesignRadioIndicator': {
              filter: 'brightness(90%)',
            },
            '&.Mui-focusVisible .CodesignRadioIndicator': {
              borderColor: controlToken.main,
              boxShadow: `0 0 0 0.25rem ${alpha(controlToken.main, 0.25)}`,
            },
            '&.Mui-disabled .CodesignRadioIndicator': {
              filter: 'none',
              opacity: 0.5,
            },
            '& .CodesignRadioIndicator': {
              alignItems: 'center',
              backgroundColor: baseBodyBackground,
              border: `1px solid ${baseBorderColor}`,
              boxSizing: 'border-box',
              color: 'transparent',
              display: 'inline-flex',
              flexShrink: 0,
              height: indicatorSize,
              justifyContent: 'center',
              transition: theme.transitions.create(['background-color', 'border-color', 'box-shadow', 'filter'], {
                duration: theme.transitions.duration.shortest,
              }),
              width: indicatorSize,
            },
            '& .CodesignRadioIndicator svg': {
              display: 'block',
              height: indicatorSize - 6,
              width: indicatorSize - 6,
            },
            '& .CodesignRadioIndicator[data-shape="default"]': {
              borderRadius: '50%',
            },
            '& .CodesignRadioIndicator[data-shape="rounded"]': {
              borderRadius: 6,
            },
            '& .CodesignRadioIndicator[data-state="checked"]': {
              backgroundColor: controlToken.main,
              borderColor: controlToken.main,
              color: '#ffffff',
            },
          }
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        select: ({ ownerState }) => ({
          minHeight: 'unset',
          padding: ownerState.size === 'small' ? '9.5px 42px 9.5px 14px' : '16.5px 42px 16.5px 14px',
        }),
        icon: {
          right: 14,
        },
      },
    },
    MuiSnackbarContent: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          fontSize: '0.875rem',
        },
      },
    },
    MuiSwitch: {
      defaultProps: {
        disableRipple: true,
      },
      styleOverrides: {
        root: ({ ownerState }) => ({
          height: ownerState.size === 'small' ? 16 : 18,
          overflow: 'visible',
          padding: 0,
          width: ownerState.size === 'small' ? 28 : 32,
        }),
        switchBase: ({ ownerState, theme }) => {
          const switchToken = getButtonToken(ownerState.color ?? 'primary')
          const isSmall = ownerState.size === 'small'
          const translateX = isSmall ? 12 : 14

          return {
            color: alpha(baseBodyColor, 0.25),
            padding: 2,
            transition: theme.transitions.create(['color', 'transform'], {
              duration: theme.transitions.duration.shortest,
            }),
            '&:active .MuiSwitch-thumb': {
              filter: 'brightness(90%)',
            },
            '&:hover': {
              backgroundColor: 'transparent',
            },
            '& .MuiSwitch-thumb': {
              backgroundColor: 'currentColor',
            },
            '&.Mui-focusVisible + .MuiSwitch-track': {
              borderColor: switchToken.main,
              boxShadow: `0 0 0 0.25rem ${alpha(switchToken.main, 0.25)}`,
            },
            '&.Mui-disabled': {
              color: alpha(baseBodyColor, 0.25),
            },
            '&.Mui-disabled + .MuiSwitch-track': {
              opacity: 0.5,
            },
            '&.Mui-checked': {
              color: '#ffffff',
              transform: `translateX(${translateX}px)`,
              '& + .MuiSwitch-track': {
                backgroundColor: switchToken.main,
                borderColor: switchToken.main,
                opacity: 1,
              },
            },
          }
        },
        thumb: ({ ownerState }) => ({
          boxShadow: 'none',
          height: ownerState.size === 'small' ? 12 : 14,
          width: ownerState.size === 'small' ? 12 : 14,
        }),
        track: {
          backgroundColor: baseSecondaryBackground,
          border: `1px solid ${baseBorderColor}`,
          boxSizing: 'border-box',
          borderRadius: 999,
          opacity: 1,
          transition: 'background-color 150ms ease-in-out, border-color 150ms ease-in-out, box-shadow 150ms ease-in-out',
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        fullWidth: true,
        variant: 'outlined',
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: '#2c3136',
          borderRadius: 6,
          fontSize: '0.75rem',
          padding: '8px 10px',
        },
        arrow: {
          color: '#2c3136',
        },
      },
    },
  }
}

export function createCodesignTheme(themeName: CodesignThemeName = 'earth') {
  const activeTheme = smartAdminAccentTokens[themeName]

  return createTheme({
    shape: {
      borderRadius: 6,
    },
    palette: {
      mode: 'light',
      primary: activeTheme,
      secondary: smartAdminAccentTokens.secondary,
      success: smartAdminAccentTokens.success,
      info: smartAdminAccentTokens.info,
      warning: smartAdminAccentTokens.warning,
      error: smartAdminAccentTokens.danger,
      fusion: smartAdminAccentTokens.fusion,
      background: {
        default: baseBodyBackground,
        paper: baseBodyBackground,
      },
      divider: baseBorderColor,
      text: {
        primary: baseBodyColor,
        secondary: 'rgba(33, 37, 41, 0.75)',
      },
      action: {
        hover: 'rgba(33, 37, 41, 0.05)',
        selected: alpha(activeTheme.main, 0.09),
        disabledBackground: baseSecondaryBackground,
      },
    },
    typography: {
      allVariants: {
        lineHeight: 1.6,
      },
      fontFamily: 'system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      fontSize: 14,
      fontWeightRegular: 400,
      fontWeightMedium: 500,
      h1: {
        fontSize: '2.5rem',
        fontWeight: 700,
        lineHeight: 1.2,
      },
      h2: {
        fontSize: '2rem',
        fontWeight: 700,
        lineHeight: 1.25,
      },
      h3: {
        fontSize: '1.5rem',
        fontWeight: 700,
        lineHeight: 1.3,
      },
      small: {
        fontSize: '0.75rem',
        lineHeight: 1.4,
      },
    },
    components: createComponents(themeName),
  })
}

const themeCache = new Map<CodesignThemeName, Theme>()

export function getCodesignTheme(themeName: CodesignThemeName = 'earth') {
  const cached = themeCache.get(themeName)

  if (cached) {
    return cached
  }

  const nextTheme = createCodesignTheme(themeName)
  themeCache.set(themeName, nextTheme)

  return nextTheme
}
